<template lang="pug">
  v-flex.pt-2.px-3.mt-2(v-if="config")
    .title Code couleurs
    v-layout.column.margin
      v-layout.align-center.mb-3
        .text-code-couleur Couleur principale
        rc-color-picker.ml-4.pl-2(
          :color="config.theme.colors.primary||'#cecece'"
          data-test="primaryColor"
          @changeColor="value => changeStyleColor('primary', value)"
        )
      v-layout.align-center
        .text-code-couleur Couleur secondaire
        rc-color-picker.ml-4.pl-2(
          :color="config.theme.colors.secondary||'#cecece'"
          data-test="secondaryColor"
          @changeColor="value => changeStyleColor('secondary', value)"
        )

    .title Choix de la couleur de fond
    v-layout.column.margin
      v-radio-group.mt-0(
        hide-details
        ref="colorModeChoice"
        data-test="colorModeColorChoice"
        :value="config.theme.hue && config.theme.hue !== 'null' ? 'sepia' : 'null'"
        @change="value => updateStyle('hue', value === 'null' ? null : '#cecece')"
      )
        v-radio.pb-3.pt-2(label="Standard" :value="'null'" :color="color" data-test="colorRadio")
        v-radio.mb-2(label="Sépia" value="sepia"  :color="color" data-test="sepiaRadio")
      div(v-if="config.theme.hue")
        .grey--text.subheading.mt-3 Couleur de fond
        v-layout
          v-flex.xs6
            div(data-test="mapColorChoice")
              v-select.pt-0.mt-0.mb-2(
                hide-details
                ref="tintModeSelect"
                :items="colorChoicesExtended"
                :value="formattedMapColorChoice"
                item-text="label"
                item-value="value"
                @change="value => { updateStyle('hue', getColorFrom(value)) }"
              )
          v-flex.xs4.offset-xs1
            rc-color-picker(
              data-test="mapColor"
              :color="backgroundColor"
              @changeColor="value => updateStyle('hue', value)"
            )
        v-slider(
          thumb-label
          :min="1" :max="10" :step="1"
          :color="color"
          label="Intensité de la couleur de fond"
          :value="config.theme.tint_intensity?config.theme.tint_intensity:3"
          @change="value => updateStyle('tint_intensity', value)"
        )

    .title(v-if="config.theme.hue") Choix de la couleur des icones
    v-layout.column.margin(v-if="config.theme.hue")
      v-radio-group.mt-0(
        :value="(config.theme.icons && config.theme.icons.default) ? 'default' : 'custom'"
        @change="value => updateStyle('icons.default', value === 'default')"
      )
        v-radio.pb-3.pt-2(color="primary" label="Standard" value="default")
        v-radio(color="primary" label="Personnalisée" value="custom")
      div(v-if="!(config.theme.icons && config.theme.icons.default)")
        .grey--text.subheading Couleur du logo
        v-layout
          v-flex.xs6
            v-select.pt-0.mt-0.mb-2(
              hide-details
              item-text="label"
              item-value="value"
              :items="colorChoices"
              :value="config.theme.icons?getKeyFrom(this.config.theme.icons.stroke, true):'custom'"
              @change="value => updateStyle('icons.stroke', getColorFrom(value))"
            )
          v-flex.xs4.offset-xs1
            rc-color-picker(
              popup="top"
              data-test="mapColor"
              :color="iconStrokeColor"
              @changeColor="value => updateStyle('icons.stroke', value)"
            )
        .grey--text.subheading.mt-3 Couleur de fond
        v-layout
          v-flex.xs6
            v-select.pt-0.mt-0.mb-2(
              hide-details
              item-text="label"
              item-value="value"
              :items="colorChoices"
              :value="config.theme.icons?getKeyFrom(this.config.theme.icons.fill, true):'custom'"
              @change="value => updateStyle('icons.fill', getColorFrom(value))"
            )
          v-flex.xs4.offset-xs1.mb-4
            rc-color-picker(
              popup="top"
              data-test="mapColor"
              :color="iconFillColor"
              @changeColor="value => updateStyle('icons.fill', value)"
            )
</template>

<script>
import RcColorPicker from '@/components/RcColorPicker.vue';
import mixin from '@/mixins/Rc.mixin.vue';

export default {
  components: {
    RcColorPicker,
  },
  mixins: [mixin],
  props: {
    config: Object,
  },
  data() {
    return {
      loading: false,
      transientParams: {
        theme: {
          colors: {},
        },
      },
    };
  },
  watch: {
    config() {
      this.transientParams = this.filterConfig(this.config);
    },
  },
  mounted() {
    this.transientParams = this.filterConfig(this.config);
  },
  computed: {
    backgroundColor() {
      // eslint-disable-next-line operator-linebreak
      return /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(this.config.theme.hue) ?
        this.config.theme.hue : this.defaultColor;
    },
    iconStrokeColor() {
      // eslint-disable-next-line operator-linebreak
      return (this.config.theme.icons && this.config.theme.icons.stroke) ?
        this.config.theme.icons.stroke : this.defaultColor;
    },
    iconFillColor() {
      // eslint-disable-next-line operator-linebreak
      return (this.config.theme.icons && this.config.theme.icons.fill) ?
        this.config.theme.icons.fill : this.defaultColor;
    },
    formattedMapColorChoice() {
      return this.getKeyFrom(this.config.theme.hue, true);
    },
    formattedIconFillChoice() {
      // eslint-disable-next-line operator-linebreak
      return this.config.theme.icons ?
        this.getKeyFrom(this.config.theme.icons.fill, true) : 'custom';
    },
    formattedIconStrokeChoice() {
      // eslint-disable-next-line operator-linebreak
      return this.config.theme.icons ?
        this.getKeyFrom(this.config.theme.icons.stroke, true) : 'custom';
    },
    isChoice() {
      return this.$route.query.context === 'choice';
    },
    color() {
      return this.isChoice ? '#e7523e' : 'primary';
    },
  },
  methods: {
    changeStyleColor(key, value) {
      this.loading = true;
      const theme = JSON.parse(JSON.stringify(this.config.theme));
      // eslint-disable-next-line default-case
      if (this.formattedMapColorChoice === key) {
        theme.hue = value;
      }
      if (this.formattedIconFillChoice === key) {
        theme.icons.fill = value;
      }
      if (this.formattedIconStrokeChoice === key) {
        theme.icons.stroke = value;
      }
      theme.colors[key] = value;
      this.$set(this.config, 'theme', theme);
      this.$emit('changed', this.filterConfig(this.config));
    },
    updateStyle(key, value) {
      this.loading = true;
      const theme = JSON.parse(JSON.stringify(this.config.theme));
      if (key === 'hue' && value === null) {
        delete theme.hue;
      } else {
        if (!theme.icons) {
          theme.icons = {
            fill: '#fff',
            stroke: '#000',
          };
        }
        Object.setByString(theme, key, value);
      }
      this.$nextTick(() => {
        this.$refs.colorModeChoice.setActiveRadio(this.config.theme.hue === 'null' ? 'null' : this.config.theme.hue);
      });
      this.$set(this.config, 'theme', theme);
      this.$emit('changed', this.filterConfig(this.config));
    },
  },
};
</script>

<style lang="scss" scoped>
  .title{
    font-family: Roboto,sans-serif;
    color: #e7523e;
    font-size: 17px !important;
  }
  .margin{
    margin: 10px 20px 30px 20px;
  }
  .text-code-couleur{
    font-family: Roboto,sans-serif;
    color: #A1A1A1;
    font-size: 16px;
    width: 160px;
    margin-right: 4px;
  }
</style>
